import { ArrowUpRight, Building2, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router";
import { useTranslation } from "@ncp/i18n";
import { Badge, Button, Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle, Skeleton } from "@ncp/ui";
import { useMergermarketCompany } from "./api";

/** Licensed-web company card plus replica deals and news that mention it. */
export function CompanySheet({ companyId, onClose, onOpenDeal }: { companyId: string | null; onClose: () => void; onOpenDeal: (replicaId: string) => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const lookup = useMergermarketCompany(companyId);
  const company = lookup.data?.company;
  const deals = lookup.data?.relatedDeals ?? [];
  const news = lookup.data?.relatedNews ?? [];

  return (
    <Sheet open={companyId !== null} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="end" width="max-w-xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 pe-8"><Building2 className="size-5" /> {company?.name ?? t("backoffice:dealWatch.companyLookup")}</SheetTitle>
          <SheetDescription className="flex flex-wrap items-center gap-2">
            {company ? <><span className="font-mono">{company.externalId}</span>{company.country ? <Badge size="sm" variant="outline">{company.country}</Badge> : null}{company.relatedIntelCount != null ? <span>{t("backoffice:dealWatch.companyIntel", { count: company.relatedIntelCount })}</span> : null}</> : null}
          </SheetDescription>
        </SheetHeader>
        <SheetBody className="space-y-5">
          {lookup.isLoading ? <Skeleton className="h-48" /> : (
            <>
              {company?.description ? <p className="text-sm leading-relaxed" dir="auto">{company.description}</p> : null}
              <div className="flex flex-wrap gap-2">
                {company?.url ? <Button size="sm" variant="outline" asChild><a href={company.url} target="_blank" rel="noreferrer"><ExternalLink /> {t("backoffice:dealWatch.openOnMergermarket")}</a></Button> : null}
                {company?.website ? <Button size="sm" variant="ghost" asChild><a href={company.website} target="_blank" rel="noreferrer"><ExternalLink /> {company.website.replace(/^https?:\/\//, "")}</a></Button> : null}
              </div>
              <section>
                <h4 className="mb-2 text-sm font-semibold">{t("backoffice:dealWatch.relatedDeals")}</h4>
                {deals.length === 0 ? <p className="text-sm text-muted-foreground">{t("misc.none")}</p> : (
                  <ul className="space-y-1.5">
                    {deals.map((d) => (
                      <li key={d.externalId}>
                        <button type="button" disabled={!d.replicaId} onClick={() => d.replicaId && onOpenDeal(d.replicaId)} className="flex w-full items-start gap-2 rounded-lg border border-sand-200 px-3 py-2 text-start text-sm hover:bg-sand-50 disabled:cursor-default">
                          <span className="min-w-0 flex-1"><span className="line-clamp-2 font-medium">{d.headline}</span><span className="font-mono text-[11px] text-muted-foreground">{d.externalId}</span></span>
                          {d.replicaId ? <ArrowUpRight className="mt-0.5 size-4 text-aegold-800" /> : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
              <section>
                <h4 className="mb-2 text-sm font-semibold">{t("backoffice:dealWatch.relatedNews")}</h4>
                {news.length === 0 ? <p className="text-sm text-muted-foreground">{t("misc.none")}</p> : (
                  <ul className="space-y-1.5">
                    {news.map((n) => (
                      <li key={n.externalId}>
                        <button type="button" onClick={() => n.replicaId ? navigate(`/news?item=${n.replicaId}`) : undefined} className="flex w-full items-start rounded-lg border border-sand-200 px-3 py-2 text-start text-sm hover:bg-sand-50">
                          <span className="line-clamp-2 font-medium">{n.headline}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
